import { RuleType, ValueSource } from 'react-querybuilder';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { MacrosFormActions } from 'src/graphql/entities/macro';
import { RelativeDate } from 'src/pages/Settings/rules/components/types';
import { BOOLEAN_CONTROL_OPTIONS } from 'src/pages/Settings/rules/rules-form-config';
import {
  ConfigActionFields,
  QueryOptions,
  RulesEngineConfig,
} from 'src/pages/Settings/rules/use-rules-engine';
import { SignalRulesEngineConfig } from 'src/pages/Settings/signal-oncall-schedule/use-signal-rules-engine';
import { BehaviorRaw } from 'src/types';
import { sanitizeJSON } from 'src/utils/strings';
import {
  ActionType,
  BehaviorTrigger,
  ConditionType,
  DataConditionOperator,
  FormControlType,
} from './behavior-config';
import {
  BehaviorConfigOption,
  BusinessHoursType,
  ConditionElement,
  ConditionGroup,
  ConditionNode,
  CreateTwilioTaskActionRule,
  CustomRuleGroupType,
  CustomRuleType,
  DataActionRule,
  DataConditionNode,
  ManageConversationLabelsActionRule,
  ManageSubAssigneesActionRule,
  RulesArray,
  RulesFormActions,
  RulesFormModel,
  RunTriageActionRule,
  SelectorValue,
  SendConversationMessageActionRule,
  SendConversationMessageSource,
  SendSlackMessageActionRule,
  SignalActionRule,
  SystemAction,
  UIBehavior,
  WebhookActionRule,
} from './behavior.types';

/**
 * Derives where a send-message action's body comes from. A stored prompt makes it an
 * AI rule; everything else — including every rule that predates the feature, and one
 * whose prompt is only whitespace — is static text. Derived rather than persisted, the
 * same call macros made for its text snippet, so the source can never disagree with
 * the data and nothing needs a backfill.
 */
export function resolveSendConversationMessageSource(
  action: Pick<SendConversationMessageActionRule, 'aiPrompt'>,
): SendConversationMessageSource {
  return action.aiPrompt?.trim()
    ? SendConversationMessageSource.AiPrompt
    : SendConversationMessageSource.StaticText;
}

export function mapFormValuesToBehaviorRequest(values: RulesFormModel, partnerId: number) {
  const formRulesModel = values.conditions;

  const requestRules = {
    elementType: 'conditionGroup',
    operator: formRulesModel.combinator,
    conditionsElements: formRulesModel.rules.map(mapUIRulesForRequest),
  };

  /**
   * Every action for now can only have a single rule, although it is using
   * react-querybuider component which creates groups of rules, allowing
   * to have multiple rules for a single action.
   */
  const formActionsModel = values.actions;
  const requestActions = formActionsModel.map(mapUIActionsForRequest);

  return {
    partnerId,

    name: values.name,
    description: values.description,
    behaviorTrigger: values.behaviorTrigger,

    conditions: JSON.stringify(requestRules),
    actions: JSON.stringify(requestActions),
  };
}

export function mapUIRulesForRequest(rulesObject: RulesArray[number]): ConditionElement {
  if (isRuleType(rulesObject)) {
    return {
      elementType: 'conditionGroup',
      operator: rulesObject.combinator,
      conditionsElements: rulesObject.rules.map(mapUIRulesForRequest),
    };
  }

  // Necessary cast because library typings expect `field` to extend string, but we're working with objects.
  const field = rulesObject.field as unknown as SelectorValue;

  switch (field.conditionType) {
    case ConditionType.ContactCenterHours:
      return {
        elementType: 'conditionNode',
        conditionType: 'businessHours',
        businessHoursType: BusinessHoursType.ContactCenter,
        operator: rulesObject.operator,
        value: getConditionValue(rulesObject),
      } as ConditionNode;
    case ConditionType.PartnerBusinessHours:
      return {
        elementType: 'conditionNode',
        conditionType: 'businessHours',
        businessHoursType: BusinessHoursType.Partner,
        operator: rulesObject.operator,
        value: getConditionValue(rulesObject),
      } as ConditionNode;
    case ConditionType.Data:
    default:
      return getDataConditionNodePayload(rulesObject, field);
  }
}

function getDataConditionNodePayload(
  rulesObject: CustomRuleType,
  field: SelectorValue,
): DataConditionNode {
  const payload = {
    elementType: 'conditionNode',
    conditionType: field.conditionType,
    attribute: field.value,
    operator: rulesObject.operator,
    value: getConditionValue(rulesObject),
  } as DataConditionNode;

  const isRollingDate = !!(
    field.uiType === FormControlType.Date &&
    Object.values(RelativeDate).includes(payload.value as RelativeDate)
  );

  if (isRollingDate) {
    payload.isRollingDate = true;
  }

  return payload;
}

// A data action's stored value is a string. A single-select carries an option object; a
// multi_select (multiple picker) carries an array of options, serialized to a JSON array of its
// option keys so it round-trips through the `::text[]` write cast (which JSON-parses the string).
function serializeDataActionValue(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }

  if (Array.isArray(value)) {
    // each element is an option object ({ value: <key> }); tolerate a bare string and drop anything
    // without a string key so the wire value is always a clean JSON array of keys (never `[null]`).
    return JSON.stringify(
      value
        .map((option) =>
          typeof option === 'string' ? option : (option as { value?: string })?.value,
        )
        .filter((optionKey): optionKey is string => typeof optionKey === 'string'),
    );
  }

  return (value as { value: string })?.value ?? '';
}

export function mapUIActionsForRequest(
  action: RulesFormActions[number] | MacrosFormActions[number],
) {
  if (isDataFormAction(action)) {
    const actionRule = action.actions?.rules?.[0] as RuleType<
      string,
      string,
      SelectorValue
    > & { field: SelectorValue };

    return {
      type: action.actionType,
      attribute: actionRule.field.value,
      value: serializeDataActionValue(actionRule.value),
    };
  }

  if (isSendSlackMessageFormAction(action)) {
    return {
      type: action.actionType,
      channel: action.actions.channel,
      message: action.actions.message,
      workspaceType: action.actions.workspaceType.value,
    };
  }

  if (isSendConversationMessageFormAction(action)) {
    const usesAiPrompt =
      action.actions.messageSource === SendConversationMessageSource.AiPrompt;

    return {
      type: action.actionType,
      isPrivate: action.actions.isPrivate.value,
      // Only the chosen branch is persisted, so a rule is never both static text and
      // an AI prompt — whatever the other branch still holds in the form. The derived
      // `messageSource` is form state and is deliberately not written.
      message: usesAiPrompt ? '' : action.actions.message,
      aiPrompt: usesAiPrompt ? action.actions.aiPrompt || null : null,
      // Absent target (gate off) maps to 'chat' on the backend.
      ...(action.actions.target ? { target: action.actions.target.value } : {}),
    };
  }

  if (isWebhookFormAction(action)) {
    return {
      type: action.actionType,
      url: action.actions.url,
      method: action.actions.method.value,
      headers: action.actions.headers
        ? (JSON.parse(sanitizeJSON(action.actions.headers)) as {
            name: string;
            value: string;
          }[])
        : '',
      body: action.actions.body,
    };
  }

  if (isSignalFormAction(action)) {
    return {
      type: action.actionType,
      operation: action.actions.operation.value,
      notificationsTargetType: action.actions.notificationsTargetType?.value,
    };
  }
  if (isManageConversationLabelsFormAction(action)) {
    return {
      type: action.actionType,
      labelIds: (
        action.actions.labelIds as {
          value: string;
        }[]
      ).map((labelId) => labelId.value),
      operation: action.actions.operation.value,
    };
  }

  if (isManageSubAssigneesFormAction(action)) {
    return {
      type: action.actionType,
      platformUserIds: (
        action.actions.platformUserIds as {
          value: string;
        }[]
      ).map((platformUserId) => platformUserId.value),
      operation: action.actions.operation.value,
    };
  }

  if (isCreateTwilioTaskFormAction(action)) {
    return {
      type: action.actionType,
      taskChannel: action.actions.taskChannel.value,
    };
  }

  if (isRunTriageFormAction(action)) {
    return {
      type: action.actionType,
      userInstructions: action.actions.userInstructions,
      conversationTriageId: action.actions.conversationTriageId?.value ?? null,
    };
  }

  return {};
}

const NUMBER_RANGE_OPERATORS = [
  DataConditionOperator.In,
  DataConditionOperator.NotIn,
  DataConditionOperator.Between,
  DataConditionOperator.NotBetween,
];
const BOOLEAN_OPERATORS = [DataConditionOperator.True, DataConditionOperator.False];

function getConditionValue(condition: CustomRuleType) {
  // Unary operators like `isEmpty` and `isNotEmpty` have no value (undefined).
  if (!condition.value) {
    return condition.value;
  }

  const isNumericField =
    typeof condition?.field !== 'string' && condition.field?.uiType === FormControlType.Number;

  if (typeof condition.value === 'number' || typeof condition.value === 'boolean') {
    return condition.value;
  }

  if (typeof condition.value === 'string') {
    if (NUMBER_RANGE_OPERATORS.includes(condition.operator)) {
      return condition.value.split(',').map((v) => Number(v));
    }

    if (BOOLEAN_OPERATORS.includes(condition.operator)) {
      return condition.operator === DataConditionOperator.True;
    }

    if (isNumericField) {
      return Number(condition.value);
    }

    return condition.value;
  }

  if (Array.isArray(condition.value)) {
    // @ts-expect-error TODO: Figure out proper typings.
    return (condition.value as BehaviorConfigOption[]).map((v) =>
      isNumericField ? Number(v.value) : v?.value,
    );
  }

  const result = condition.value.value;
  return isNumericField ? Number(result) : result;
}

function isRuleType(rule: RulesArray[number]): rule is CustomRuleGroupType {
  return 'rules' in rule;
}

export function mapBehaviorResponseToUI(behavior: BehaviorRaw): UIBehavior {
  let conditions: ConditionGroup = {
    conditionsElements: [],
    operator: 'and',
    elementType: 'conditionGroup',
  };
  let actions: SystemAction[] = [];

  try {
    conditions = JSON.parse(behavior.conditions) as ConditionGroup;
  } catch (e) {
    console.error(e);
  }

  try {
    actions = JSON.parse(behavior.actions) as SystemAction[];
  } catch (e) {
    console.error(e);
  }

  return {
    id: behavior.id,
    behaviorId: behavior.id,
    createdAt: behavior.createdAt,
    updatedAt: behavior.updatedAt,
    name: behavior.name,
    description: behavior.description,
    isDefault: behavior.partnerId === PLATFORM_PARTNER_ID,
    partnerId: behavior.partnerId,
    behaviorTrigger: behavior.behaviorTrigger as BehaviorTrigger,
    conditions,
    actions,
    labelAssignments: behavior.labelAssignments,
  };
}

export function mapBehaviorToFormValues(
  behavior: UIBehavior,
  config: RulesEngineConfig,
): RulesFormModel {
  const result = {
    ...behavior,
    behaviorTrigger: behavior.behaviorTrigger as BehaviorTrigger,
    conditions: {
      combinator: behavior.conditions.operator,
      not: false,
      rules: behavior.conditions.conditionsElements.map((condition) =>
        mapConditionToFormValues(condition, config),
      ),
    },
    actions: behavior.actions.map((action) => mapActionToFormValues(action, config.actions)),
  };

  // @ts-expect-error Impossible to override native lib typings (field is expected to be a string).
  return result;
}

export function mapConditionToFormValues(
  rule: ConditionNode | ConditionGroup,
  config: RulesEngineConfig | SignalRulesEngineConfig,
): RulesArray[number] {
  const { conditions } = config;

  if (rule.elementType === 'conditionNode') {
    const field = conditions.find((item) => {
      if ('attribute' in rule) {
        return item.value === rule.attribute;
      }
      if ('businessHoursType' in rule) {
        return item.value === (rule.businessHoursType as string);
      }

      if ((rule as ConditionNode).conditionType === ConditionType.Actor) {
        return item.conditionType === ConditionType.Actor;
      }
      return false;
    })!;

    // force fetch options for dynamic options conditions if no options
    if (rule.value && !field.fieldOptions.length && field.optionsQuery) {
      config?.queryOptions?.[field.optionsQuery]?.onInputChange?.(rule.value as string);
    }

    return {
      id: crypto.randomUUID(),
      field,
      operator: rule.operator,
      // @ts-expect-error TODO: Figure out how to derive and cover typings for different types of values for different attributes.
      value: getValueByUiType(rule, config.conditions),
      valueSource: 'value' as ValueSource,
    };
  }

  return {
    id: crypto.randomUUID(),
    combinator: rule.operator,
    not: false,
    rules: rule.conditionsElements.map((item) =>
      mapConditionToFormValues(item, config),
    ) as RulesArray,
  };
}

// Only an array-typed field (e.g. multi_select, whose accessor carries an array cast such as
// `::text[]`) stores its value as a JSON array string of option keys. For any other List field a
// leading `[` is just a literal value, so the parse is scoped to array-typed accessors.
const ARRAY_CAST_ACCESSOR_PATTERN = /::\w+\[\]$/;

function parseMultiSelectListValue(value: unknown, accessor: unknown): unknown {
  const isArrayField =
    typeof accessor === 'string' && ARRAY_CAST_ACCESSOR_PATTERN.test(accessor);

  if (isArrayField && typeof value === 'string' && value.startsWith('[')) {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }
  return value;
}

function getValueByUiType(rule: ConditionNode | DataActionRule, fields: SelectorValue[]) {
  const field = fields.find((item) => {
    if ('attribute' in rule) {
      return item.value === rule.attribute;
    }
    if ('businessHoursType' in rule) {
      return item.value === (rule.businessHoursType as string);
    }

    if ((rule as ConditionNode).conditionType === ConditionType.Actor) {
      return item.conditionType === ConditionType.Actor;
    }
    return false;
  })!;

  switch (field?.uiType) {
    case FormControlType.List: {
      // a multi_select stores its value as a JSON array of option keys (see
      // serializeDataActionValue); the parse is scoped to its array-cast accessor so a single-select
      // value that merely starts with `[` is left untouched.
      const listValue = parseMultiSelectListValue(rule.value, field.value);
      if (Array.isArray(listValue)) {
        return listValue.map((item) =>
          (field.fieldOptions || []).find((option) => option.value === item),
        );
      }
      return (field.fieldOptions || []).find((item) => item.value === listValue)!;
    }
    default:
      if ('operator' in rule && NUMBER_RANGE_OPERATORS.includes(rule.operator)) {
        return (rule.value as number[]).join(',');
      }
      return rule.value;
  }
}

export function mapActionToFormValues(
  action: SystemAction,
  configActions: ConfigActionFields,
  queryOptions?: QueryOptions,
) {
  if ('attribute' in action) {
    const configOption = configActions.data?.find((item) => item.value === action.attribute);
    // force fetch options for dynamic options conditions if no options
    if ('value' in action && configOption?.optionsQuery && !configOption.fieldOptions.length) {
      queryOptions?.[configOption.optionsQuery].onInputChange?.(action.value);
    }
  }

  switch (action.type) {
    case ActionType.Data: {
      const actionRules = configActions[action.type];
      const field = actionRules.find((item) => item.value === action.attribute)!;

      return {
        id: crypto.randomUUID(),
        actionType: action.type,
        actions: {
          id: crypto.randomUUID(),
          combinator: 'and',
          not: false,
          rules: [
            {
              id: crypto.randomUUID(),
              operator: '=',
              valueSource: 'value' as ValueSource,
              field,
              value: getValueByUiType(action, actionRules),
            },
          ],
        },
      };
    }

    case ActionType.Webhook: {
      const actionRules = configActions[action.type];

      return {
        id: crypto.randomUUID(),
        actionType: action.type,
        actions: {
          ...action,
          method:
            actionRules
              .find((item) => item.name === 'method')
              ?.fieldOptions?.find((item) => item.value === action.method) || '',
          headers: action.headers ? JSON.stringify(action.headers) : '',
        },
      };
    }

    case ActionType.SendSlackMessage: {
      const actionRules = configActions[action.type];
      return {
        id: crypto.randomUUID(),
        actionType: action.type,
        actions: {
          ...action,
          workspaceType:
            actionRules
              .find((item) => item.name === 'workspaceType')
              ?.fieldOptions?.find((item) => item.value === action.workspaceType) || '',
        },
      };
    }

    case ActionType.SendConversationMessage: {
      const actionRules = configActions[action.type];
      const targetOptions = actionRules?.find((item) => item.name === 'target')?.fieldOptions;
      return {
        id: crypto.randomUUID(),
        actionType: action.type,
        actions: {
          message: action.message,
          messageSource: resolveSendConversationMessageSource(action),
          aiPrompt: action.aiPrompt ?? '',
          isPrivate:
            BOOLEAN_CONTROL_OPTIONS.find((option) => option.value === action.isPrivate) ||
            BOOLEAN_CONTROL_OPTIONS[0],
          // 'chat' when absent (backward compat). With the attribute hidden (gate off), a
          // saved target rides through form state as a synthetic option — never rendered,
          // but an edit-save must not silently rewrite 'sms_session' to 'chat'.
          target:
            targetOptions?.find((item) => item.value === (action.target ?? 'chat')) ??
            (action.target ? { value: action.target, label: action.target } : null),
        },
      };
    }

    case ActionType.Signal: {
      const actionRules = configActions[action.type];

      return {
        id: crypto.randomUUID(),
        actionType: action.type,
        actions: {
          ...action,
          operation:
            actionRules
              .find((item) => item.name === 'operation')
              ?.fieldOptions?.find((item) => item.value === action.operation) || '',
          notificationsTargetType:
            actionRules
              .find((item) => item.name === 'notificationsTargetType')
              ?.fieldOptions?.find((item) => item.value === action.notificationsTargetType) ||
            '',
        },
      };
    }

    case ActionType.ManageConversationLabels: {
      const actionRules = configActions[action.type];
      const operationAttributes = actionRules.find(
        (actionRule) => actionRule.name === 'operation',
      );
      const labelIdsAttribute = actionRules.find(
        (actionRule) => actionRule.name === 'labelIds',
      );

      return {
        id: crypto.randomUUID(),
        actionType: action.type,
        actions: {
          ...action,
          operation:
            operationAttributes?.fieldOptions?.find(
              (item) => item.value === action.operation,
            ) || '',
          labelIds:
            action.labelIds?.map((labelId) => {
              const option = labelIdsAttribute?.fieldOptions?.find(
                (item) => item.value === labelId,
              );
              return (
                option ?? {
                  value: labelId,
                  label: labelId,
                }
              );
            }) || [],
        },
      };
    }

    case ActionType.ManageSubAssignees: {
      const actionRules = configActions[action.type];
      const operationAttributes = actionRules.find(
        (actionRule) => actionRule.name === 'operation',
      );
      const platformUserIdsAttribute = actionRules.find(
        (actionRule) => actionRule.name === 'platformUserIds',
      );

      return {
        id: crypto.randomUUID(),
        actionType: action.type,
        actions: {
          ...action,
          operation:
            operationAttributes?.fieldOptions?.find(
              (item) => item.value === action.operation,
            ) || '',
          platformUserIds:
            action.platformUserIds?.map((platformUserId) => {
              const option = platformUserIdsAttribute?.fieldOptions?.find(
                (item) => item.value === platformUserId,
              );
              return (
                option ?? {
                  value: platformUserId,
                  label: platformUserId,
                }
              );
            }) || [],
        },
      };
    }

    case ActionType.CreateTwilioTask: {
      const actionRules = configActions[action.type];
      return {
        id: crypto.randomUUID(),
        actionType: action.type,
        actions: {
          ...action,
          taskChannel:
            actionRules
              .find((item) => item.name === 'taskChannel')
              ?.fieldOptions?.find((item) => item.value === action.taskChannel) || '',
        },
      };
    }

    case ActionType.RunTriage: {
      const actionRules = configActions[action.type];
      return {
        id: crypto.randomUUID(),
        actionType: action.type,
        actions: {
          userInstructions: action.userInstructions ?? '',
          conversationTriageId: findFieldOption(
            actionRules,
            'conversationTriageId',
            action.conversationTriageId,
          ),
        },
      };
    }

    // More in the future
  }
}

function findFieldOption(
  actionRules: ConfigActionFields[ActionType.RunTriage],
  attributeName: string,
  value: unknown,
): (BehaviorConfigOption & { value: string }) | null {
  const option = actionRules
    ?.find((item) => item.name === attributeName)
    ?.fieldOptions?.find((item) => item.value === value);

  return typeof option?.value === 'string' ? { ...option, value: option.value } : null;
}

function isDataFormAction(action: RulesFormModel['actions'][number]): action is {
  id: string;
  actionType: ActionType.Data;
  actions: CustomRuleGroupType;
} {
  return action.actionType === ActionType.Data;
}

function isSendSlackMessageFormAction(action: RulesFormModel['actions'][number]): action is {
  id: string;
  actionType: ActionType.SendSlackMessage;
  actions: SendSlackMessageActionRule & {
    workspaceType: { label: string; id: string; value: string };
  };
} {
  return action.actionType === ActionType.SendSlackMessage;
}

function isSendConversationMessageFormAction(
  action: RulesFormModel['actions'][number],
): action is {
  id: string;
  actionType: ActionType.SendConversationMessage;
  actions: SendConversationMessageActionRule & {
    isPrivate: { label: string; id: string; value: boolean };
    target: { label: string; id: string; value: string } | null;
  };
} {
  return action.actionType === ActionType.SendConversationMessage;
}

function isWebhookFormAction(action: RulesFormModel['actions'][number]): action is {
  id: string;
  actionType: ActionType.Webhook;
  actions: WebhookActionRule & {
    method: { label: string; id: string; value: string };
  };
} {
  return action.actionType === ActionType.Webhook;
}

function isSignalFormAction(action: RulesFormModel['actions'][number]): action is {
  id: string;
  actionType: ActionType.Signal;
  actions: SignalActionRule & {
    operation: { label: string; id: string; value: string };
    notificationsTargetType: { label: string; id: string; value: string } | null;
  };
} {
  return action.actionType === ActionType.Signal;
}

function isManageConversationLabelsFormAction(
  action: RulesFormModel['actions'][number],
): action is {
  id: string;
  actionType: ActionType.ManageConversationLabels;
  actions: ManageConversationLabelsActionRule & {
    operation: { label: string; id: string; value: string };
    labelIds: { label: string; id: string; value: string }[];
  };
} {
  return action.actionType === ActionType.ManageConversationLabels;
}

function isManageSubAssigneesFormAction(action: RulesFormModel['actions'][number]): action is {
  id: string;
  actionType: ActionType.ManageSubAssignees;
  actions: ManageSubAssigneesActionRule & {
    operation: { label: string; id: string; value: string };
    platformUserIds: { label: string; id: string; value: string }[];
  };
} {
  return action.actionType === ActionType.ManageSubAssignees;
}

function isCreateTwilioTaskFormAction(action: RulesFormModel['actions'][number]): action is {
  id: string;
  actionType: ActionType.CreateTwilioTask;
  actions: CreateTwilioTaskActionRule & {
    taskChannel: { label: string; id: string; value: string };
  };
} {
  return action.actionType === ActionType.CreateTwilioTask;
}

function isRunTriageFormAction(action: RulesFormModel['actions'][number]): action is {
  id: string;
  actionType: ActionType.RunTriage;
  actions: RunTriageActionRule & {
    conversationTriageId: (BehaviorConfigOption & { value: string }) | null;
  };
} {
  return action.actionType === ActionType.RunTriage;
}
